import {
  Alert,
  Button,
  ButtonGroup,
  ButtonProps,
  closeCurrentDialog,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Show} from 'platform/foundation';

import {useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  AutomarketplPlatformInput,
  AutomarketplPlatformInputSchema,
  useGetAutomarketplInspectionQuestionsQuery,
  useEditPlatformSettingsMutation,
  useGetPlatformSettingsQuery,
  VehiclePlatformAdvertisingSettingsRequestBody,
  VehiclePlatformAdvertisingSettingsResponseBody,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, useAdPlatformsVehicleUpdateDialog} from '@dms/shared';
import {$AdvertisingSettingsRequestBody, Form} from '@dms/teas';

import {appendSuffix, buildArray, safeParseJSON, suffixTestId, TestIdProps} from 'shared/web';

import {AdvertisingAttributeField} from './AdvertisingAttributeField';
import {AutomarketplEurotaxSelection} from './AutomarketplEurotaxSelection';
import {AutomarketplInputEditor} from './AutomarketplInputEditor';

type PlatformEditFormValues = Pick<VehiclePlatformAdvertisingSettingsRequestBody, 'attributes'> & {
  platformInput?: AutomarketplPlatformInput | null;
};
const REGISTRATION_PLATE_CODE = 'registration_plate';
const isNotRegistrationPlate = (
  attribute: Pick<VehiclePlatformAdvertisingSettingsResponseBody['attributes'][number], 'code'>
) => attribute.code !== REGISTRATION_PLATE_CODE;
const TRANSLATED_AUTOMARKETPL_INSPECTION_QUESTION_IDS = new Set([
  1,
  ...Array.from({length: 74}, (_, index) => index + 10),
]);

function getAutomarketplFormInput(
  input: Record<string, unknown> | null | undefined,
  questionIds: number[] | undefined
) {
  const parsed = AutomarketplPlatformInputSchema.pick({inspekcja: true}).parse({
    inspekcja: input?.inspekcja,
  });
  const rozmiarOgumienia =
    typeof input?.rozmiarOgumienia === 'string' ? input.rozmiarOgumienia.trim() : undefined;
  return {
    ...parsed,
    ...(isNilOrEmpty(rozmiarOgumienia) ? {} : {rozmiarOgumienia}),
    ...(typeof input?.zalacznikSpecyfikacja === 'string'
      ? {zalacznikSpecyfikacja: input.zalacznikSpecyfikacja}
      : {}),
    ...(typeof input?.kodEurotax === 'number' ? {kodEurotax: input.kodEurotax} : {}),
    ...(questionIds
      ? {
          inspekcja: [
            ...(parsed.inspekcja ?? []),
            ...questionIds.flatMap((pytanieId) =>
              parsed.inspekcja?.some((answer) => answer.pytanieId === pytanieId)
                ? []
                : [{pytanieId, wartosc: 1 as const}]
            ),
          ],
        }
      : {}),
  };
}

interface PlatformEditProps extends TestIdProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  platformName: string;
  isDisabled: boolean;
}

export function PlatformEdit(props: PlatformEditProps) {
  const isAutomarketpl = props.platformCode === AdvertisingPlatformCodeEnum.AUTOMARKETPL;
  const {
    data: inspectionQuestionsResponse,
    isLoading: areQuestionsLoading,
    isError: hasQuestionsError,
    refetch: reloadQuestions,
  } = useGetAutomarketplInspectionQuestionsQuery(
    {vehicleId: props.vehicleId},
    {skip: !isAutomarketpl}
  );
  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();

  const [editPlatformSettings, {isLoading: isEditLoading}] = useEditPlatformSettingsMutation();
  const [isUploading, setIsUploading] = useState(false);

  const {
    data: platformSettings,
    isLoading,
    isError,
  } = useGetPlatformSettingsQuery(
    {
      vehicleId: props.vehicleId,
      platformCode: props.platformCode,
    },
    {
      skip: isNilOrEmpty(props.vehicleId) || isNilOrEmpty(props.platformCode),
    }
  );

  const handleSubmit = async (
    values: Pick<PlatformEditFormValues, 'attributes'> & {platformInputJson?: string}
  ) => {
    const requestBody: VehiclePlatformAdvertisingSettingsRequestBody = {
      attributes: values.attributes.filter(isNotRegistrationPlate),
    };
    if (isAutomarketpl) {
      const input = safeParseJSON(
        values.platformInputJson ?? 'null',
        AutomarketplPlatformInputSchema.nullable()
      );
      if (input === undefined) {
        return;
      }
      if (input === null) {
        requestBody.platformInput = null;
      } else {
        const {rozmiarOgumienia, ...rest} = input;
        requestBody.platformInput = {
          ...rest,
          ...(typeof rozmiarOgumienia === 'string' ? {rozmiarOgumienia} : {}),
        };
      }
    }

    await editPlatformSettings({
      vehicleId: props.vehicleId,
      platformCode: props.platformCode,
      vehiclePlatformAdvertisingSettingsRequestBody: requestBody,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .then(() => {
        showNotification.success(
          i18n.t('general.notifications.advertisement.windshieldDataEditSubtitle')
        );
        updateVehicleAds(props.vehicleId, props.platformCode);
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isLoading={isLoading || (isAutomarketpl && areQuestionsLoading)}
      isError={isError}
      isEmpty={isNilOrEmpty(platformSettings)}
    >
      <Form<
        PlatformEditFormValues,
        VehiclePlatformAdvertisingSettingsResponseBody,
        Pick<PlatformEditFormValues, 'attributes'> & {platformInputJson?: string}
      >
        formId={appendSuffix('platformEditForm', props['data-testid'])}
        onSubmit={handleSubmit}
        initialValues={platformSettings}
        schema={$AdvertisingSettingsRequestBody}
        getFormValues={(settings) => ({
          attributes: settings.attributes.filter(isNotRegistrationPlate),
          ...(isAutomarketpl
            ? {
                platformInput: getAutomarketplFormInput(
                  settings.platformInput,
                  inspectionQuestionsResponse?.inspectionQuestions.map(
                    (question) => question.pytanieId
                  )
                ),
              }
            : {}),
        })}
        getRequestBody={(values) => ({
          attributes: values.attributes.map((attribute) => ({
            ...attribute,
            value: attribute.override ? attribute.value : null,
          })),
          ...(isAutomarketpl
            ? {platformInputJson: JSON.stringify(values.platformInput ?? null)}
            : {}),
        })}
        render={(formRendererProps) => {
          const {handleSubmit, FieldArray} = formRendererProps;

          const actions = buildArray<ButtonProps>()
            .add({
              title: i18n.t('general.actions.discard'),
              variant: 'secondary',
              onClick: closeCurrentDialog,
            })
            .whenNot(props.isDisabled, {
              title: i18n.t('general.actions.confirm'),
              onClick: handleSubmit,
              isLoading: isEditLoading || isUploading,
            });

          return (
            <form onSubmit={handleSubmit}>
              <Grid columns={12}>
                <GridItem span={12}>
                  <Alert
                    data-testid={suffixTestId('platformEdit-alert', props)}
                    type="inline"
                    variant="info"
                    title={i18n.t('general.notifications.advertisement.platformDataEdit')}
                  />
                </GridItem>
                <FieldArray<VehiclePlatformAdvertisingSettingsRequestBody> name="attributes">
                  {({fields}) =>
                    fields.map((name) => (
                      <AdvertisingAttributeField
                        name={name}
                        key={name}
                        isDisabled={props.isDisabled}
                        vehicleId={props.vehicleId}
                        platformCode={props.platformCode}
                        platformName={props.platformName}
                      />
                    ))
                  }
                </FieldArray>
                <Show when={isAutomarketpl}>
                  <GridItem span={12}>
                    <AutomarketplEurotaxSelection
                      vehicleId={props.vehicleId}
                      isDisabled={props.isDisabled}
                    />
                  </GridItem>
                  <GridItem span={12}>
                    <Show when={hasQuestionsError}>
                      <Alert
                        type="inline"
                        variant="warning"
                        title={i18n.t('entity.workshop.labels.notAvailable')}
                      />
                      <Button
                        variant="link"
                        title={i18n.t('general.actions.retry')}
                        onClick={() => {
                          void reloadQuestions();
                        }}
                      />
                    </Show>
                    <DataStatus isLoading={areQuestionsLoading}>
                      <AutomarketplInputEditor
                        isDisabled={props.isDisabled}
                        onUploadingChange={setIsUploading}
                        options={
                          inspectionQuestionsResponse
                            ? {
                                inspection: inspectionQuestionsResponse.inspectionQuestions.map(
                                  (question) => {
                                    const key = `page.vehicleDetail.automarketplInspection.questions.${question.pytanieId}`;
                                    const hasTranslation =
                                      TRANSLATED_AUTOMARKETPL_INSPECTION_QUESTION_IDS.has(
                                        question.pytanieId
                                      );
                                    return {
                                      categoryId: question.kategoriaId,
                                      value: question.pytanieId,
                                      label: hasTranslation
                                        ? i18n.t(`${key}.label`)
                                        : question.trescDluga || question.trescKrotka,
                                      helperText:
                                        question.pytanieId === 1
                                          ? i18n.t(`${key}.help`)
                                          : question.podpowiedz || undefined,
                                    };
                                  }
                                ),
                              }
                            : undefined
                        }
                      />
                    </DataStatus>
                  </GridItem>
                </Show>
                <GridItem span={12}>
                  <ButtonGroup align="right" buttons={actions} />
                </GridItem>
              </Grid>
            </form>
          );
        }}
      />
    </DataStatus>
  );
}
